import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const FeaturedExperiences = () => {
  const navigate = useNavigate();

  const experiences = [
    {
      id: 1,
      title: "White Water Rafting",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5",
      description: "Experience the thrill of navigating through Dandeli\'s rapids on the Kali River",
      duration: "3-4 hours",
      difficulty: "Moderate",
      price: "₹899",
      highlights: ["Professional Guide", "Safety Equipment", "Photography", "Refreshments"],
      bestTime: "Oct - May"
    },
    {
      id: 2,
      title: "Jungle Safari",
      image: "https://images.unsplash.com/photo-1549366021-9f761d040a94",
      description: "Explore Dandeli Wildlife Sanctuary and spot tigers, elephants, and exotic birds",
      duration: "2-3 hours", 
      difficulty: "Easy",
      price: "₹699",
      highlights: ["Wildlife Spotting", "Expert Naturalist", "4WD Vehicle", "Binoculars"],
      bestTime: "Year Round"
    },
    {
      id: 3,
      title: "Adventure Trekking",
      image: "https://images.unsplash.com/photo-1551632811-561732d1e306",
      description: "Trek through dense forests to hidden waterfalls and scenic viewpoints",
      duration: "4-6 hours",
      difficulty: "Challenging", 
      price: "₹799",
      highlights: ["Mountain Views", "Waterfall Visit", "Nature Photography", "Packed Lunch"],
      bestTime: "Nov - Mar"
    }
  ];

  const handleBookExperience = (experience) => {
    navigate('/activities-experiences', { state: { selectedActivity: experience } });
  };

  const handleViewAllExperiences = () => {
    navigate('/activities-experiences');
  };

  return (
    <section className="py-16 bg-muted">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            Featured Experiences
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
            Embark on unforgettable adventures that showcase the best of Dandeli's natural wonders
          </p>
        </div>

        {/* Experiences Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {experiences?.map((experience) => (
            <div
              key={experience?.id}
              className="bg-card rounded-2xl shadow-organic overflow-hidden hover-lift group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={experience?.image}
                  alt={experience?.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-organic duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Difficulty Badge */}
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-heading font-semibold ${
                  experience?.difficulty === 'Easy' ? 'bg-success text-success-foreground' :
                  experience?.difficulty === 'Moderate' ? 'bg-warning text-warning-foreground' :
                  'bg-error text-error-foreground'
                }`}>
                  {experience?.difficulty}
                </div>

                {/* Price */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                  <span className="text-sm font-heading font-bold text-primary">
                    {experience?.price}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                  {experience?.title}
                </h3>
                
                <p className="text-text-secondary mb-4 font-body">
                  {experience?.description}
                </p>

                {/* Experience Details */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center">
                    <Icon name="Clock" size={16} className="text-primary mr-2" />
                    <div>
                      <p className="text-sm text-text-secondary font-caption">Duration</p>
                      <p className="text-sm font-heading font-semibold text-text-primary">
                        {experience?.duration}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Calendar" size={16} className="text-primary mr-2" />
                    <div>
                      <p className="text-sm text-text-secondary font-caption">Best Time</p>
                      <p className="text-sm font-heading font-semibold text-text-primary">
                        {experience?.bestTime}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <h4 className="text-sm font-heading font-semibold text-text-primary mb-2">
                    What's Included:
                  </h4>
                  <div className="grid grid-cols-1 gap-1">
                    {experience?.highlights?.slice(0, 2)?.map((highlight, index) => (
                      <div key={index} className="flex items-center text-sm text-text-secondary">
                        <Icon name="Check" size={12} className="text-success mr-2 flex-shrink-0" />
                        <span className="font-caption">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="default"
                    size="default"
                    iconName="Activity"
                    iconPosition="left"
                    fullWidth
                    onClick={() => handleBookExperience(experience)}
                    className="hover:scale-105 transition-organic"
                  >
                    Book Experience
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    iconName="Info"
                    iconPosition="left"
                    fullWidth
                    onClick={() => handleBookExperience(experience)}
                    className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
                  >
                    More Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Experiences CTA */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleViewAllExperiences}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            Explore All Activities
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedExperiences;